import React, { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { v4 } from 'uuid';
import { Box, Typography, CircularProgress, Tooltip, IconButton } from '@material-ui/core';
import {
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { ToggleButtonGroup, ToggleButton, Pagination } from '@material-ui/lab';
import { filter, slice } from 'lodash';
import { mapStatusCodeToLabel } from 'src/utils/claim';
import countKeyWordsInClaim from './search';
import { calculateNumberOfPages, generatePageRangeHeader } from './utils';
import {
  SUMMARY_VIEW,
  GRID_CLAIMS_PER_PAGE,
  TABLE_CLAIMS_PER_PAGE
} from './constants';
import GridView from './GridView';
import TableView from './TableView';
import { formatNumber } from 'src/utils/numbers';
import claimService from 'src/services/claimService';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import * as XLSX from 'xlsx';
import moment from 'moment';



const GRID_VISIBILITY_THRESHOLD = 300;

/**
 * @param {{ claims: object[], filters: {}, isLoading: boolean }} param 
 * @returns {object[]}
 */
function filterClaims({ claims, filters, isLoading }) {
  function claimHasKeywords(claim) {
    // match all claims if there are no filters selected
    // or the claims are still being fetched from the API
    if (!filters
      || isLoading
      || filters?.allFilters?.length === 0) {
      return true;
    }

    return claim.keyWordCount > 0;
  }

  const addKeywordCountToClaim = isLoading
    ? (claim) => claim
    : countKeyWordsInClaim;

  const claimsWithKeywordCounts = claims.map(claim => {
    return addKeywordCountToClaim(claim, filters);
  });
  const results = slice(filter(claimsWithKeywordCounts, claimHasKeywords));
  results.sort((claimA, claimB) => claimB.keyWordCount - claimA.keyWordCount);

  return results;
}

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiTablePagination-toolbar': {
      // hide table paginator component
      display: 'none'
    }
  },
  title: {
    position: 'relative',
    '&:after': {
      position: 'absolute',
      bottom: -8,
      left: 0,
      content: '" "',
      height: 3,
      width: 48,
      backgroundColor: theme.palette.primary.main
    }
  },
  sortButton: {
    textTransform: 'none',
    letterSpacing: 0,
    marginRight: theme.spacing(2)
  },
  resultControl: {
    position: 'sticky',
    top: 180,
    height: 72,
    backgroundColor: theme.palette.background.dark,
    zIndex: 100,
  },
  resultControlLowered: {
    top: 230,
  },
  scrollBorder: {
    borderBottom: '1px solid rgba(139, 139, 139, 0.12)',
  },
  main: {
    padding: theme.spacing(0, 0.25),
  }
}));

function Results({
  className,
  claims,
  filterCriteria,
  isLoading,
  haveAllClaimsLoaded,
  view,
  onViewChange,
  page,
  onPageChange,
  onScroll,
  scrollLocation,
  cardClickHandler,
  ...rest
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const gridRef = useRef();

  const claimsPerPage = view === SUMMARY_VIEW.grid
    ? GRID_CLAIMS_PER_PAGE
    : TABLE_CLAIMS_PER_PAGE;

  const claimsWithKeys = useMemo(() => {
    if (claims.length === 0 && isLoading) {
      return new Array(claimsPerPage).fill({});
    }

    return claims.map(claim => ({
      ...claim,
      claimStatus: mapStatusCodeToLabel(claim.claimStatus),
      key: v4()
    }));
  }, [claims, isLoading]);

  const claimsData = filterClaims({
    claims: claimsWithKeys,
    filters: filterCriteria,
    isLoading,
  });

  if (haveAllClaimsLoaded) {
    claimsData.sort((claimA, claimB) => {
      return claimB?.claimStatus?.charCodeAt(0) - claimA?.claimStatus?.charCodeAt(0);
    });
  }

  const handleDownloadExcel = async () => {
    try {

      // Fetch full details for each claimId
      const claimIds = claimsData.map(claim => claim.claimIdentifier)
      const details = await claimService.getBulkVendorClaim(claimIds);

      // Flatten or map details as needed for Excel
      const excelData = details?.claims?.map(detail => ({
        ClaimID: detail.claimInformation.claimNumber,
        DealerCode: detail.dealerInformation.dealerCode,
        DealerName: detail.dealerInformation.dealerName,
        Status: detail.claimInformation.claimStatus,
        Disposition: detail.claimInformation.claimDisposition,
        ShipperNumber: detail.claimInformation.trackingNumber,
        OMCNumber: detail.claimInformation.orderMasterControlNumber,

        Claimant: detail.claimContactInformation.contactName,
        ClaimantEmail: detail.claimContactInformation.emailAddress,
        ClaimantPhone: detail.claimContactInformation.phoneNumber,
        Type: detail.claimInformation.claimType,
        ClaimReason: detail.claimInformation.claimDamageReason,
        DateCreated: moment(detail.claimInformation.claimDateCreated).format('MM/DD/YYYY hh:mm A'),
        Parts: detail.claimParts.map(part => (`Part Number:${part.partNumber}-Quantity:${part.quantity}`)).join(', '),
        DealerAddress: detail.dealerInformation.dealerAddress,
        DealerCity: detail.dealerInformation.dealerCity,
        DealerState: detail.dealerInformation.dealerState,
        DealerZip: detail.dealerInformation.dealerZip,
        // Add more fields as needed from detail
      }));

      // Generate worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, t('glassClaims'));

      // Trigger download
      const dateString = moment().format('YYYYMMDD_HHmmss');

      XLSX.writeFile(workbook, `glassClaims${dateString}.xlsx`);
    } catch (err) {
      alert(t('failedToDownloadClaims') + ": " + err.message);
    }
  };

  const handlePageChange = (_, value) => onPageChange(value);

  const handleModeChange = (_, value) => {
    let newClaimsPerPageAmount;
    if (value === SUMMARY_VIEW.grid) {
      newClaimsPerPageAmount = GRID_CLAIMS_PER_PAGE;
    } else {
      newClaimsPerPageAmount = TABLE_CLAIMS_PER_PAGE;
    }

    const numPagesNeeded = calculateNumberOfPages(
      claimsData.length,
      newClaimsPerPageAmount
    );

    if (page > numPagesNeeded) {
      onPageChange(numPagesNeeded);
    }

    onViewChange(value);
  };

  const handleScroll = () => {
    if (gridRef.current) {
      const rect = gridRef.current.getBoundingClientRect();
      if (rect.top < GRID_VISIBILITY_THRESHOLD) {
        onScroll(gridRef.current.offsetTop);
      } else {
        onScroll(0);
      }
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const TotalClaimsMarkup = () => {
    if (haveAllClaimsLoaded) {
      const total = formatNumber(claimsData.length, { useGrouping: true });
      return ` ${total} claims`;
    }

    return (
      <>
        {' '}<CircularProgress size={12} /> claims
      </>
    );
  }

  const ViewToggleButton = () => {
    if (view === SUMMARY_VIEW.grid) {
      return (
        <ToggleButton
          selected
          value={SUMMARY_VIEW.table}
          disabled={isLoading}
        >
          <ViewModuleIcon />
        </ToggleButton>
      );
    }
    return (
      <ToggleButton
        selected
        value={SUMMARY_VIEW.grid}
        disabled={isLoading}
      >
        <ViewListIcon />
      </ToggleButton>
    );
  }

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Box
        className={clsx(
          classes.resultControl,
          filterCriteria.hasActiveFilters && classes.resultControlLowered,
          scrollLocation && classes.scrollBorder
        )}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        mb={2}
      >
        <Typography className={classes.title} variant="h5" color="textPrimary">
          Showing
          {generatePageRangeHeader({
            page,
            perPage: claimsPerPage,
            count: claimsData.length,
            isLoading
          })}
          of
          {TotalClaimsMarkup()}
        </Typography>
        <Box display="flex" alignItems="center">
          <Tooltip title={t('export')} arrow>
            <IconButton className={classes.exportButton} size="small" onClick={handleDownloadExcel}>
              <DownloadIcon fontSize="small" className={classes.exportIcon} />
            </IconButton>
          </Tooltip>
          <ToggleButtonGroup
            exclusive
            onChange={handleModeChange}
            size="small"
            value={view}
          >
            {ViewToggleButton()}
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Box ref={gridRef} component="main" className={classes.main}>
        {view === SUMMARY_VIEW.grid
          ? <GridView isLoading={isLoading} claims={claimsData} page={page} cardClickHandler={cardClickHandler} />
          : <TableView isLoading={isLoading} claims={claimsData} page={page} cardClickHandler={cardClickHandler} />
        }
      </Box>
      <Box mt={6} component="footer" display="flex" justifyContent="center">
        {claimsData.length > 6 && (
          <Pagination
            page={page}
            count={calculateNumberOfPages(claimsData.length, claimsPerPage)}
            onChange={handlePageChange}
          />
        )}
      </Box>
    </div>
  );
}

Results.propTypes = {
  className: PropTypes.string,
  filterCriteria: PropTypes.object,
  claims: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  haveAllClaimsLoaded: PropTypes.bool.isRequired,
  view: PropTypes.string.isRequired,
  onViewChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onScroll: PropTypes.func.isRequired,
  scrollLocation: PropTypes.number.isRequired,
  cardClickHandler: PropTypes.func,
};

export default Results;
